import type { MedplumClient } from "@medplum/core";
import type { CalAdminClient } from "@/lib/cal/admin";
import {
  type AvailabilityFormValues,
  buildAvailabilityFormValues,
} from "@/lib/cal/availability-mapper";
import { type CalSchedule, createCalClient } from "@/lib/cal/client";
import { isCalNotFoundError } from "@/lib/errors/cal";
import { Practice } from "@/models/practice/Practice";
import { Room } from "@/models/practice/Room";
import { Provider } from "@/models/provider/Provider";

export const ROOM_AVAILABILITY_SCHEDULE_NAME = "Vibrant Room Availability";

/**
 * Room availability only becomes enforceable after the room's default schedule is the dedicated
 * Vibrant-managed schedule. This is intentionally conservative: if Cal state drifts, we fail open
 * and stop enforcing instead of trusting an unrelated default schedule.
 */
export function isCustomRoomAvailabilitySchedule({
  scheduleName,
}: {
  scheduleName?: string;
}): boolean {
  return scheduleName === ROOM_AVAILABILITY_SCHEDULE_NAME;
}

interface LoggerLike {
  warn(message: string, metadata?: Record<string, unknown>): void;
}

interface ReadProviderAvailabilityInput {
  medplum: MedplumClient;
  calAdmin: CalAdminClient;
  provider: Provider;
  logger: LoggerLike;
  practiceTimeZone?: string;
  allowDefaultWhenUnavailable?: boolean;
}

interface ReadProviderAvailabilityResult {
  schedule?: AvailabilityFormValues;
  scheduleId?: number;
  hasCalendarLink: boolean;
}

interface ReadRoomAvailabilityInput {
  medplum: MedplumClient;
  calAdmin: CalAdminClient;
  room: Room;
  logger: LoggerLike;
  practiceTimeZone?: string;
}

interface ReadRoomAvailabilityResult {
  schedule: AvailabilityFormValues;
  scheduleId?: number;
  hasCustomSchedule: boolean;
}

/**
 * Loads a provider's default schedule without mutating Cal linkage.
 */
export async function readProviderAvailability({
  medplum,
  calAdmin,
  provider,
  logger,
  practiceTimeZone,
  allowDefaultWhenUnavailable = true,
}: ReadProviderAvailabilityInput): Promise<ReadProviderAvailabilityResult> {
  const practice =
    practiceTimeZone !== undefined
      ? undefined
      : await provider.getPractice(medplum).catch(() => undefined);
  const managedUser = await Provider.getManagedUserForProvider(calAdmin, provider);
  const scheduleId = managedUser?.defaultScheduleId ?? undefined;
  const timeZoneFallback = managedUser?.timeZone || practiceTimeZone || practice?.timezone;

  if (!managedUser || !scheduleId) {
    if (!allowDefaultWhenUnavailable) {
      return {
        hasCalendarLink: false,
      };
    }

    return {
      schedule: buildAvailabilityFormValues({
        schedule: null,
        timeZoneFallback,
        isDefault: true,
      }),
      hasCalendarLink: false,
    };
  }

  const calCredentials = await provider.getCalCredentials(medplum);
  if (!calCredentials) {
    if (!allowDefaultWhenUnavailable) {
      return {
        scheduleId,
        hasCalendarLink: false,
      };
    }

    return {
      schedule: buildAvailabilityFormValues({
        schedule: null,
        timeZoneFallback,
        isDefault: true,
      }),
      scheduleId,
      hasCalendarLink: false,
    };
  }

  let schedule: CalSchedule | undefined;
  let isScheduleMissing = false;
  const cal = await createCalClient(calCredentials);
  try {
    schedule = await cal.getSchedule(scheduleId);
  } catch (error) {
    if (!isCalNotFoundError(error)) {
      throw error;
    }

    logger.warn("Provider schedule missing", {
      providerId: provider.id,
      scheduleId,
      allowDefaultWhenUnavailable,
    });
    isScheduleMissing = true;
  }

  if (isScheduleMissing && !allowDefaultWhenUnavailable) {
    return {
      scheduleId,
      hasCalendarLink: true,
    };
  }

  return {
    schedule: buildAvailabilityFormValues({
      schedule,
      timeZoneFallback,
      isDefault: true,
    }),
    scheduleId,
    hasCalendarLink: true,
  };
}

/**
 * Loads a room schedule without mutating Cal linkage.
 */
export async function readRoomAvailability({
  medplum,
  calAdmin,
  room,
  logger,
  practiceTimeZone,
}: ReadRoomAvailabilityInput): Promise<ReadRoomAvailabilityResult> {
  const practiceIdToLoad = practiceTimeZone !== undefined ? undefined : room.practiceId;
  const practice = practiceIdToLoad
    ? await Practice.getById(medplum, practiceIdToLoad).catch(() => undefined)
    : undefined;
  const managedUser = await Room.getManagedUserForRoom(calAdmin, room);
  const scheduleId = managedUser?.defaultScheduleId ?? undefined;
  const timeZoneFallback = practiceTimeZone || practice?.timezone;
  const calCredentials = await room.getCalCredentials(medplum);

  let schedule: CalSchedule | undefined;
  if (scheduleId && calCredentials) {
    const cal = await createCalClient(calCredentials);
    try {
      schedule = await cal.getSchedule(scheduleId);
    } catch (error) {
      if (!isCalNotFoundError(error)) {
        throw error;
      }

      logger.warn("Room schedule missing; using defaults", {
        roomId: room.id,
        practiceId: room.practiceId,
        scheduleId,
      });
    }
  }

  const hasCustomSchedule = isCustomRoomAvailabilitySchedule({
    scheduleName: schedule?.name,
  });

  return {
    schedule: buildAvailabilityFormValues({
      // Managed users can hold unrelated default schedules. Only use the saved Vibrant room
      // schedule as source of truth; otherwise fall back to the standard room baseline.
      schedule: hasCustomSchedule ? schedule : null,
      timeZoneFallback,
      nameOverride: ROOM_AVAILABILITY_SCHEDULE_NAME,
      isDefault: true,
    }),
    scheduleId,
    hasCustomSchedule,
  };
}
